// src/services/api.js
// FIXED: Now uses idToken for API authentication (CIAM with OIDC scopes)
// CHANGED: accessToken from OIDC scopes is for Microsoft endpoints, not our API
// CHANGED: Matches working scanService.js token logic

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://driftalarm-functions-bbfmfuh5hmdsfcc7.eastus-01.azurewebsites.net/api';

console.log('API Service initialized with base URL:', API_BASE_URL);

// Helper to get auth token from MSAL
// FIXED: Returns idToken which is valid for our API with CIAM
async function getAuthToken() {
  try {
    // Get MSAL instance from window (set in index.js)
    const msalInstance = window.msalInstance;
    if (!msalInstance) {
      console.log('MSAL not initialized - proceeding without auth token');
      return null;
    }

    const accounts = msalInstance.getAllAccounts();
    if (accounts.length === 0) {
      console.log('No authenticated accounts - proceeding without auth token');
      return null;
    }

    const tokenRequest = {
      scopes: ['openid', 'profile', 'email'],
      account: accounts[0],
    };

    const response = await msalInstance.acquireTokenSilent(tokenRequest);

    // FIXED: With OIDC scopes (openid, profile, email), use idToken for our API
    // The accessToken from these scopes is for Microsoft endpoints, not our API
    const token = response.idToken;

    console.log('Successfully acquired auth token');
    return token;
  } catch (error) {
    // If MSAL needs interactive re-auth (refresh token expired, CIAM session lapsed),
    // fall back to loginRedirect instead of silently returning null. Otherwise authenticated
    // POSTs go out with no Authorization header and the API 401s with a generic message.
    if (error.name === 'InteractionRequiredAuthError') {
      console.warn('Silent token acquire requires interaction — triggering loginRedirect');
      try {
        const msalInstance = window.msalInstance;
        const accounts = msalInstance?.getAllAccounts() || [];
        await msalInstance.loginRedirect({
          scopes: ['openid', 'profile', 'email'],
          account: accounts[0],
        });
      } catch (redirectError) {
        console.error('loginRedirect failed:', redirectError.message);
      }
      // loginRedirect navigates away; this return is for the (unlikely) case it doesn't.
      return null;
    }
    console.log('Failed to acquire token (will proceed without):', error.message);
    return null;
  }
}

const scanService = {
  /**
   * Start a fast scan with optional tier-based features
   * @param {string} target - Domain or IP to scan
   * @param {object} options - Scan options
   * @param {boolean} options.includeVulnCheck - Include vulnerability scanning (Standard+)
   * @param {boolean} options.includeValidation - Include vulnerability validation (Pro+)
   */
  async startFastScan(target, options = {}) {
    console.log('API: Sending fast scan request for:', target);
    console.log('API: Scan options:', options);
    console.log('API: Using base URL:', API_BASE_URL);

    const token = await getAuthToken();
    if (!token) {
      throw new Error('Sign-in expired. Please refresh the page and sign in again.');
    }
    const headers = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    };

    const url = `${API_BASE_URL}/scan-fast`;
    console.log('API: Full URL:', url);

    // Build request body with target and options
    const requestBody = {
      target,
      includeVulnCheck: options.includeVulnCheck || false,
      includeValidation: options.includeValidation || false
    };

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(requestBody),
    });

    console.log('API: Response status:', response.status);

    if (!response.ok) {
      const errorText = await response.text();
      console.error('API: Error response:', errorText);
      throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
    }

    const data = await response.json();
    console.log('API: Parsed response data:', data);
    return data;
  },

  async getScanStatus(scanId) {
    console.log('API: Getting scan status for:', scanId);

    const token = await getAuthToken();
    if (!token) {
      throw new Error('Sign-in expired. Please refresh the page and sign in again.');
    }
    const headers = {
      'Authorization': `Bearer ${token}`,
    };

    const url = `${API_BASE_URL}/scan-status/${scanId}`;
    console.log('API: Fetching:', url);

    const response = await fetch(url, {
      headers,
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
    }
    
    const data = await response.json();
    console.log('API: Status response:', data);
    return data;
  },

  async getScanResult(scanId) {
    console.log('API: Getting scan result for:', scanId);
    
    const token = await getAuthToken();
    const headers = {};
    
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const url = `${API_BASE_URL}/scan-result/${scanId}`;
    console.log('API: Fetching:', url);

    const response = await fetch(url, {
      headers,
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
    }
    
    const data = await response.json();
    console.log('API: Result response:', data);
    return data;
  },

  async listScans() {
    console.log('API: Listing all scans');
    
    const token = await getAuthToken();
    const headers = {};
    
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const url = `${API_BASE_URL}/scan-list`;
    console.log('API: Fetching:', url);

    const response = await fetch(url, {
      headers,
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
    }
    
    const data = await response.json();
    console.log('API: List response:', data);
    return data;
  },

  // ADDED: Get SCRUTINIZE scan result
  async getScrutinizeResult(scanId) {
    console.log('API: Getting SCRUTINIZE result for:', scanId);

    const token = await getAuthToken();
    const headers = {};

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const url = `${API_BASE_URL}/scan-scrutinize-result?scanId=${scanId}`;
    console.log('API: Fetching:', url);

    const response = await fetch(url, {
      headers,
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
    }

    const data = await response.json();
    console.log('API: SCRUTINIZE result response:', data);
    return data;
  },

  // Generate PDF report for a scan
  // Set force=true to regenerate even if cached PDF exists
  async generatePDF(scanId, scanType = 'scrutinize', force = false) {
    console.log(`API: Generating PDF for ${scanType} scan: ${scanId}${force ? ' (force regenerate)' : ''}`);

    const token = await getAuthToken();
    const headers = {
      'Content-Type': 'application/json',
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const url = `${API_BASE_URL}/scan-generate-pdf`;
    console.log('API: Fetching:', url);

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify({ scanId, scanType, force }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
    }

    const data = await response.json();
    console.log('API: PDF generation response:', data);
    return data;
  },

  // Download PDF - generates if needed and opens in new tab
  // Set force=true to regenerate even if cached PDF exists
  async downloadPDF(scanId, scanType = 'scrutinize', force = false) {
    try {
      const result = await this.generatePDF(scanId, scanType, force);

      if (result.success && result.pdfUrl) {
        // Open PDF in new tab for download
        window.open(result.pdfUrl, '_blank');
        return result;
      } else {
        throw new Error('PDF URL not available');
      }
    } catch (error) {
      console.error('API: Error downloading PDF:', error);
      throw error;
    }
  }
};

export { scanService };